import { format } from 'date-fns';

import { IValidatorFunction, IValidator } from '@origin-digital/validation';
import { ECardIssuerCode, isMastercard } from '@origin-digital/payments';
import { getCookie } from '@origin-digital/platform-helpers';

import {
  authenticationCookie,
  dateFormat,
  IDivisionIdWithLpg,
  invalidRedirectParam,
  lpgDivisionId,
} from 'src/consts';
import { EPaymentType } from 'src/interfaces';

export const isKrakenId = (value: string) => /^A-[\dA-Z]{8}$/i.test(value);

export const krakenValidator: IValidator = ({ error }) => (value) => {
  if (!isKrakenId(value)) {
    return error;
  }
};

export const contractAccountIdValidator: IValidator = ({ error }) => (value) => {
  if (!/\d{12}/.test(value.replace(/\s/g, ''))) {
    return error;
  }
};

export const formatValidator = (
  value: string,
  errors: {
    contractAccountid: string;
    kraken: string;
  },
) => {
  if (value.charAt(0) === 'A') {
    return krakenValidator({ error: errors.kraken });
  }

  return contractAccountIdValidator({ error: errors.contractAccountid });
};

export const reduceValidators = (value: string, validators: IValidatorFunction[]): string =>
  validators.reduce<string>((errorMessage, validator: IValidatorFunction) => {
    if (!!errorMessage) {
      return errorMessage;
    }

    return validator(value) || '';
  }, '');

export const getPaymentCardType = (cardNumber: string): ECardIssuerCode => {
  return isMastercard(cardNumber) ? ECardIssuerCode.MasterCard : ECardIssuerCode.Visa;
};

export const formatDate = (timestamp: string) => format(new Date(timestamp), dateFormat);

export const formatReceiptNumber = (receiptNumber: string): string =>
  `${receiptNumber.replace(/(.{3})/g, '$1 ')}`.trim();

export const isMyAccount = (): boolean => window.location.pathname.includes('/my-account/');
export const isKrakenMyAccount = (): boolean => window.location.pathname.includes('/my/');

export const isLpg = (divisionId: IDivisionIdWithLpg | ''): boolean => divisionId === lpgDivisionId;

export const isAuthenticated = () => {
  /**
   * TODO PBS-1262
   *
   * This is a temporary implementation to validate whether a customer
   * is logged in or not. This will be uplifted to use the generic
   * logged in validation once it becomes available to use.
   */
  const authenticationExpiry = getCookie(authenticationCookie);
  return authenticationExpiry ? Date.now() <= parseInt(authenticationExpiry, 10) : false;
};

export const hasCaid = (): boolean =>
  new URLSearchParams(window.location.search.slice(1)).has('caid');

export const hasKrakenId = (): boolean =>
  new URLSearchParams(window.location.search.slice(1)).has('accountNumber');

export const hasLength = (min: number, max = Infinity) => (value: string) => {
  if (!value) {
    return false;
  }

  const length = value.replace(/\s/g, '').length;
  return length >= min && length <= max;
};

export const hasAmount = (amount: string): boolean => !isNaN(parseFloat(amount));
export const hasValidAmount = (amount: string): boolean =>
  hasAmount(amount) && parseFloat(amount) >= 100;

export const toCents = (amount: number): number => Math.round(amount * 100);

export const appendInvalidRedirectUrl = (route: string) => {
  const params = new URLSearchParams(window.location.search);
  const invalidRedirect = params.get('invalidRedirect');
  if (invalidRedirect) {
    return `${route}${
      route !== '/'
        ? `?${invalidRedirectParam}=${encodeURIComponent(invalidRedirect)}`
        : invalidRedirect
    }`;
  }
  return route;
};

export const getSalesforceContractObject = (customerNumber, accBalance) => ({
  contractAccountId: customerNumber,
  lastInvoiceId: null,
  paymentType: EPaymentType.Invoice,
  accountBalance: accBalance,
  amountDue: 0,
  amountOverdue: 0,
  isAmountDuePaidByDirectDebit: false,
  payOnTime: {
    discountAmount: null,
    includedInAmountDue: false,
  },
  instalment: {
    amountDue: null,
    amountOverdue: null,
  },
});
